import React, { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { formatApiErrorDetail } from "@/lib/api";
import Logo from "@/components/Logo";
import { Sparkles, KeyRound, Mail, Eye, EyeOff } from "lucide-react";

export default function Login() {
  const { user, login, loading } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  if (!loading && user) {
    const subStatus = user.subscription_status || "active";
    if (user.role === "student" && ["pending_approval", "pending_payment"].includes(subStatus) && !user.trial_active) {
      return <Navigate to="/pending-approval" replace />;
    }
    return <Navigate to={user.role === "admin" ? "/admin" : "/student"} replace />;
  }

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const u = await login(email.trim(), password);
      toast.success(`Welcome back, ${u.name}!`);
      if (u.role === "student" && ["pending_approval", "pending_payment"].includes(u.subscription_status || "active") && !u.trial_active) {
        navigate("/pending-approval");
      } else {
        navigate(u.role === "admin" ? "/admin" : "/student");
      }
    } catch (err) {
      const msg = formatApiErrorDetail(err.response?.data?.detail) || err.message;
      setError(msg);
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col lg:flex-row bg-canvas">
      {/* Left visual panel */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden bg-sky">
        <div className="absolute inset-0 opacity-90"
             style={{ backgroundImage: "radial-gradient(circle at 20% 20%, rgba(255,209,102,0.25) 0, transparent 40%), radial-gradient(circle at 80% 80%, rgba(255,90,95,0.35) 0, transparent 45%)" }} />
        <div className="relative z-10 flex flex-col justify-between p-12 text-white w-full">
          <Logo size="lg" className="[&_.text-ink]:text-white" />
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/20 backdrop-blur-sm text-sm font-bold mb-6">
              <Sparkles className="w-4 h-4" /> Fun. Fast. Focused practice.
            </div>
            <h1 className="font-heading text-5xl font-bold leading-tight mb-4">
              Learn abacus,<br />the fun way.
            </h1>
            <p className="text-lg text-white/90 max-w-md">
              Solve exercises on an interactive soroban, beat your best score, and become a math whiz — one bead at a time.
            </p>
          </div>
          <div className="text-sm text-white/70">© {new Date().getFullYear()} MathWhiz Beads</div>
        </div>
      </div>

      {/* Right form */}
      <div className="flex-1 flex items-center justify-center p-6 sm:p-10">
        <div className="w-full max-w-md">
          <div className="lg:hidden mb-8"><Logo size="md" /></div>

          <h2 className="font-heading text-4xl font-bold text-ink mb-2">Sign in</h2>
          <p className="text-stone mb-8">Welcome back! Let's practice.</p>

          <form onSubmit={onSubmit} className="space-y-5" data-testid="login-form">
            <div>
              <label className="block text-sm font-bold text-ink mb-2" htmlFor="email">Email</label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-pebble" />
                <input
                  id="email"
                  data-testid="login-email-input"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="w-full h-14 pl-12 pr-4 rounded-2xl border-2 border-cloud focus:border-sky focus:outline-none text-base font-medium bg-white"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-bold text-ink mb-2" htmlFor="password">Password</label>
              <div className="relative">
                <KeyRound className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-pebble" />
                <input
                  id="password"
                  data-testid="login-password-input"
                  type={showPassword ? "text" : "password"}
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="w-full h-14 pl-12 pr-12 rounded-2xl border-2 border-cloud focus:border-sky focus:outline-none text-base font-medium bg-white"
                />
                <button
                  type="button"
                  data-testid="toggle-password-visibility"
                  onClick={() => setShowPassword((s) => !s)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-pebble hover:text-ink"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            {error && (
              <div data-testid="login-error" className="text-sm font-semibold text-watermelon bg-watermelon-light px-4 py-3 rounded-2xl">
                {error}
              </div>
            )}

            <button
              data-testid="login-submit-button"
              type="submit"
              disabled={submitting}
              className="w-full h-14 rounded-full bg-watermelon text-white font-heading font-bold text-lg btn-tactile hover:bg-watermelon-hover disabled:opacity-70"
            >
              {submitting ? "Signing in…" : "Sign in"}
            </button>

            <div className="text-center">
              <a
                href="/forgot-password"
                data-testid="forgot-password-link"
                className="text-sm font-bold text-sky-hover hover:underline"
              >
                Forgot password?
              </a>
            </div>
          </form>

          <p className="mt-8 text-sm text-stone text-center">
            New student? <a href="/signup" data-testid="signup-link" className="text-sky-hover font-bold hover:underline">Create an account</a>
          </p>
          <p className="mt-3 text-sm text-stone text-center">
            <a href="/about" data-testid="about-link" className="text-stone font-bold hover:text-ink hover:underline">← Learn about MathWhiz Beads</a>
          </p>
        </div>
      </div>
    </div>
  );
}
